/**
 * Single source of truth for what a customer view says while there is no
 * data to show, keyed by the customer's lifecycle status (ENTRAID-259).
 *
 * Before this, each tab hard-coded its own empty state, and the Activity tab
 * told a customer still waiting for Global Admin consent that the "first
 * collection was triggered automatically after deployment". Every tab
 * (Overview, Security, Activity, Configuration, Alerts) and the Dashboard now
 * asks lifecycleNotice() instead, so the tabs cannot drift apart.
 *
 * The statuses are the backend state machine in
 * portal/backend/lib/customer-status.mjs (mirrored in ./customer-status.js).
 * A status this file does not know renders an explicit "Status unavailable"
 * notice rather than a misleading one.
 */
import { STATUS } from './customer-status.js';
import { deployFailureText } from './deploy-failure.js';

export const SUPPORT_EMAIL = 'support@ipc-global.com';

export const AUDIENCE = Object.freeze({
  CUSTOMER: 'customer', // a customer admin, or an operator "Viewing as client"
  OPERATOR: 'operator',
});

export const PHASE = Object.freeze({
  ONBOARDING: 'onboarding',
  DEPLOYING: 'deploying',
  FIRST_COLLECTION: 'first-collection',
  READY: 'ready',
  FAILED: 'failed',
  ENDING: 'ending',
  ENDED: 'ended',
  UNKNOWN: 'unknown',
});

export const ACTION = Object.freeze({
  CONTINUE_ONBOARDING: 'continue-onboarding', // link into the onboarding wizard
  COPY_CONSENT_LINK: 'copy-consent-link', // operator: fetch and copy the admin-consent URL
  COPY_ONBOARDING_LINK: 'copy-onboarding-link', // operator: copy the link to send the customer
  CHECK_AGAIN: 'check-again', // reload the customer record
  CONTACT_SUPPORT: 'contact-support',
});

/**
 * Coarse lifecycle phase for a customer, given whether any collection run
 * exists yet. deployed/active without a run is the "first collection" phase.
 */
export function lifecyclePhase(status, { hasData = false } = {}) {
  switch (status) {
    case STATUS.REGISTERED:
    case STATUS.CONSENT_PENDING:
    case STATUS.CONSENT_CONFIRMED:
      return PHASE.ONBOARDING;
    case STATUS.DEPLOYING:
      return PHASE.DEPLOYING;
    case STATUS.DEPLOYED:
    case STATUS.ACTIVE:
      return hasData ? PHASE.READY : PHASE.FIRST_COLLECTION;
    case STATUS.DEPLOY_FAILED:
      return PHASE.FAILED;
    case STATUS.PENDING_DECOMMISSION:
      return PHASE.ENDING;
    case STATUS.DECOMMISSIONED:
    case STATUS.SUSPENDED_TRUST_LOST:
      return PHASE.ENDED;
    default:
      return PHASE.UNKNOWN;
  }
}

/** Path of the onboarding wizard for this customer (same shape the backend emails). */
export function onboardingPath(customerId) {
  return `/onboarding?customerId=${encodeURIComponent(customerId ?? '')}`;
}

const continueOnboarding = (customerId, label = 'Continue onboarding') => ({
  type: ACTION.CONTINUE_ONBOARDING,
  label,
  href: onboardingPath(customerId),
});
const copyOnboardingLink = { type: ACTION.COPY_ONBOARDING_LINK, label: 'Copy onboarding link' };
const copyConsentLink = { type: ACTION.COPY_CONSENT_LINK, label: 'Copy consent link' };
const checkAgain = { type: ACTION.CHECK_AGAIN, label: 'Check again' };
const contactSupport = {
  type: ACTION.CONTACT_SUPPORT,
  label: SUPPORT_EMAIL,
  href: `mailto:${SUPPORT_EMAIL}`,
};

/**
 * What to show when a customer view has no data yet.
 *
 * @param {object|null} customer  the customer record from GET /customers/{id}
 * @param {{ audience?: 'customer'|'operator', hasData?: boolean }} [options]
 *   hasData: true when at least one collection run exists.
 * @returns {null | {
 *   status: string|null, phase: string, tone: 'amber'|'red'|'slate',
 *   title: string, short: string, message: string, detail: string|null,
 *   pollDeployStatus: boolean, actions: Array<{type: string, label: string, href?: string}>
 * }} null when the customer is deployed and has data (nothing to explain).
 */
export function lifecycleNotice(customer, { audience = AUDIENCE.CUSTOMER, hasData = false } = {}) {
  const status = customer?.status ?? null;
  const phase = lifecyclePhase(status, { hasData });
  if (phase === PHASE.READY) return null;

  const op = audience === AUDIENCE.OPERATOR;
  const id = customer?.customerId;
  const base = { status, phase, detail: null, pollDeployStatus: false };

  switch (status) {
    case STATUS.REGISTERED:
      return {
        ...base,
        tone: 'amber',
        title: 'Onboarding not started',
        short: 'Onboarding not started',
        message: op
          ? 'The customer has not connected a Microsoft Entra tenant yet. Send their administrator the onboarding link.'
          : 'Connect your Microsoft Entra tenant to start. Security results, activity, and alerts appear after the collector is deployed and the first data collection finishes.',
        actions: op ? [copyOnboardingLink] : [continueOnboarding(id)],
      };

    case STATUS.CONSENT_PENDING:
      return {
        ...base,
        tone: 'amber',
        title: 'Waiting for Global Admin consent',
        short: 'Waiting for consent',
        message: op
          ? 'A Global Administrator of the customer\'s Microsoft Entra tenant has not granted onGuard read-only consent yet. Copy the consent link and send it to them.'
          : 'A Global Administrator of your Microsoft Entra tenant must grant onGuard read-only consent. Deployment and the first data collection start after consent.',
        actions: op
          ? [customer?.entraTenantId ? copyConsentLink : copyOnboardingLink]
          : [continueOnboarding(id)],
      };

    case STATUS.CONSENT_CONFIRMED:
      return {
        ...base,
        tone: 'amber',
        title: 'Consent granted, ready to deploy',
        short: 'Ready to deploy',
        message: op
          ? 'Consent is granted. The customer still needs to create the cross-account role and deploy the collector in their AWS account.'
          : 'Next, create the cross-account role and deploy the collector in your AWS account. The first data collection starts automatically after deployment.',
        actions: op ? [copyOnboardingLink] : [continueOnboarding(id)],
      };

    case STATUS.DEPLOYING:
      return {
        ...base,
        tone: 'amber',
        title: 'Deployment in progress',
        short: 'Deploying',
        message: op
          ? 'The collector is being deployed in the customer\'s AWS account. This usually takes a few minutes; the first data collection starts automatically when it finishes.'
          : 'The collector is being deployed in your AWS account. This usually takes a few minutes; the first data collection starts automatically when it finishes.',
        pollDeployStatus: true,
        actions: [],
      };

    case STATUS.DEPLOYED:
    case STATUS.ACTIVE:
      return {
        ...base,
        tone: 'amber',
        title: 'First collection in progress',
        short: 'First collection in progress',
        message: 'The collector is deployed and the first data collection started automatically. Results typically appear within a few minutes.',
        actions: [checkAgain],
      };

    case STATUS.DEPLOY_FAILED:
      return {
        ...base,
        tone: 'red',
        title: 'Deployment failed',
        short: 'Deployment failed',
        message: op
          ? 'The collector stack failed to deploy in the customer\'s AWS account. Fix the cause shown below, then retry the deployment from the onboarding wizard.'
          : 'The collector could not be deployed in your AWS account. Contact support and we will help you fix the cause, then retry the deployment.',
        // An operator sees the raw reason; a customer gets a coded one (ENTRAID-350 review L5), shown as plain text.
        detail: op ? (customer?.deployFailureReason || null) : deployFailureText(customer?.deployFailureReason),
        actions: op
          ? [continueOnboarding(id, 'Retry deployment')]
          : [continueOnboarding(id, 'Retry deployment'), contactSupport],
      };

    case STATUS.PENDING_DECOMMISSION:
      return {
        ...base,
        tone: 'amber',
        title: 'Subscription ending',
        short: 'Decommissioning',
        message: op
          ? 'The AWS Marketplace subscription was canceled. This deployment is waiting to be decommissioned.'
          : 'Your AWS Marketplace subscription was canceled, so this deployment is scheduled to be decommissioned. To keep onGuard running, contact support.',
        actions: op ? [] : [contactSupport],
      };

    case STATUS.DECOMMISSIONED:
      return {
        ...base,
        tone: 'slate',
        title: 'Deployment decommissioned',
        short: 'Decommissioned',
        message: op
          ? 'The collector was removed and no new data is collected for this customer.'
          : 'onGuard was removed from your AWS account and no new data is collected. To start again, contact support.',
        actions: op ? [] : [contactSupport],
      };

    case STATUS.SUSPENDED_TRUST_LOST:
      return {
        ...base,
        tone: 'red',
        title: 'Suspended: trust role lost',
        short: 'Suspended',
        message: op
          ? 'The onGuard trust role could not be assumed for 7 days, so the Entra credential was revoked and customer access is blocked. Restore access once the customer has restored the trust stack.'
          : 'onGuard can no longer reach your AWS account because the onGuard trust stack was removed or changed. Ask your AWS administrator to restore it, then contact support.',
        actions: op ? [] : [contactSupport],
      };

    default:
      return {
        ...base,
        tone: 'slate',
        title: 'Status unavailable',
        short: 'Status unavailable',
        message: op
          ? `This customer record has an unrecognized status (${status ?? 'none'}). Check the record before taking action.`
          : 'We could not determine the status of your deployment. Contact support if this persists.',
        actions: op ? [] : [contactSupport],
      };
  }
}
